import {Component, EventEmitter, Input, Output, inject, OnChanges, DestroyRef, OnDestroy, SimpleChanges, booleanAttribute} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BasicChartComponent } from './components/basic-chart/basic-chart.component';
import { WidgetContentService } from '../../../shared/services/content/widget-content.service';
import { ThemedImagesService } from '../../../shared/services/themed-images/themed-images.service';
import {Widget} from '../models/widget';
import {DynamicMaterialTableComponent} from './components/dynamic-material-table/dynamic-material-table.component';
import {LoadingIndicatorComponent} from '../../../shared/components/loading-indicator/loading-indicator.component';
import {MatTabsModule} from '@angular/material/tabs';
import {WidgetTable} from '../models/widget-table';
import {WidgetTableColumn} from '../models/widget-table-column';
import {Subscription} from 'rxjs';

@Component({
  standalone: true,
  imports: [MatTabsModule, MatIcon, BasicChartComponent, DynamicMaterialTableComponent, LoadingIndicatorComponent],
  selector: 'via-widget-content',
  templateUrl: './widget-content.component.html',
  styleUrl: './widget-content.component.scss'
})
export class WidgetContentComponent implements OnChanges, OnDestroy {
  @Input() widget: Widget;
  @Input({ transform: booleanAttribute }) showCloseButton = false;
  @Input() isLiveData = false;
  @Output() closeClicked = new EventEmitter<void>();

  private themedImages = inject(ThemedImagesService);
  private sanitizer = inject(DomSanitizer);
  private widgetContentService = inject(WidgetContentService);
  private destroyRef = inject(DestroyRef);

  themedSvgHtml: SafeHtml | null = null;
  isLoading = false;
  showErrorState = false;
  private loadedWidget: Widget | null = null;
  private contentLoadSubscription: Subscription | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldReloadContent(changes)) {
      this.loadContent();
    }
  }

  ngOnDestroy(): void {
    this.contentLoadSubscription?.unsubscribe();
  }

  get shouldUseDynamicMaterialTable(): boolean {
    return this.isLiveData && this.hasDynamicColumns;
  }

  get displayWidget(): Widget | null {
    return this.loadedWidget ?? this.widget ?? null;
  }

  get dynamicColumns(): WidgetTableColumn[] {
    return this.displayWidget?.tables?.[0]?.columns ?? [];
  }

  get primaryTable(): WidgetTable | undefined {
    return this.displayWidget?.tables?.[0];
  }

  get canCloseWidget(): boolean {
    return this.showCloseButton === true;
  }

  private get hasDynamicColumns(): boolean {
    return (this.displayWidget?.tables?.[0]?.columns?.length ?? 0) > 0;
  }

  private shouldReloadContent(changes: SimpleChanges): boolean {
    if (changes['isLiveData'] && changes['isLiveData'].previousValue !== changes['isLiveData'].currentValue) {
      return true;
    }

    if (!changes['widget']) {
      return false;
    }

    const prevWidget: Widget = changes['widget'].previousValue;
    const nextWidget: Widget = changes['widget'].currentValue;

    if (!prevWidget || !nextWidget) {
      return true;
    }

    if (prevWidget.widgetKey !== nextWidget.widgetKey) {
      return true;
    }

    return false;
  }

  private loadContent(): void {
    this.contentLoadSubscription?.unsubscribe();
    this.loadedWidget = null;
    this.themedSvgHtml = null;

    if (!this.widget) {
      this.isLoading = false;
      return;
    }

    const widget = this.widget;
    this.isLoading = true;
    this.showErrorState = false;
    this.contentLoadSubscription = this.widgetContentService
      .getContent(widget, this.isLiveData)
      .subscribe({
        next: (content) => {
          try {
            this.loadedWidget = content;
            this.updateThemedImage();
          } finally {
            this.isLoading = false;
          }
        },
        error: () => {
          this.isLoading = false;
          this.showErrorState = true;
        },
      });
  }

  getDataset(table: WidgetTable): object[] {
    if (table.dataset) {
      return table.dataset as [];
    } else {
      return [];
    }
  }

  private updateThemedImage(): void {
    const widget = this.displayWidget;

    if (widget?.illustrationType !== 'themed-image') return;
    const key = widget?.themedImageKey;
    if (!key) return;

    const svg = this.themedImages.themedImage(key);
    this.themedSvgHtml = this.sanitizer.bypassSecurityTrustHtml(svg);
  }
}
